/**
 * A supplier's invoice, keyed from the paper in the order it reads:
 * supplier, their invoice number, date, then the lines.
 *
 * Built for speed the way voucher entry is: the item field takes a code,
 * a name or a scanned barcode (a carton's barcode picks the carton), the
 * cost is filled from what was last paid this supplier, Enter moves along
 * the row and on to the next, and Ctrl+Enter saves.
 *
 * Every figure on screen is computed exactly as the server computes it
 * (string arithmetic, halala rounding per line), so the total the clerk
 * checks against the paper is the total that gets posted.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Autocomplete, Box, Button, IconButton, MenuItem, Paper, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { useTranslation } from 'react-i18next'

import {
  checkPurchaseDuplicate, createPurchase, fetchInvItems, fetchLastCost, fetchStores,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type InvItem } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useLedgerBalances } from '../store/useLedgerBalances'
import { useUnsavedChanges } from '../store/UnsavedChanges'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import LedgerPicker from '../vouchers/LedgerPicker'
import Keycap from '../components/Keycap'
import ScreenTitle from '../components/ScreenTitle'
import { addMoney, mulMoney, sanitize, subMoney } from '../vouchers/money'
import { toHijri } from '../reports/hijri'
import { today } from '../dates'
import { c, fmtMoney } from '../theme'
import { lineAmount } from './qty'

interface Line {
  key: number
  item: InvItem | null
  unit: string
  qty: string
  cost: string
  discount: string
  hint?: string
}

let nextKey = 1
const blank = (): Line => ({ key: nextKey++, item: null, unit: '', qty: '', cost: '', discount: '' })

const VAT: Record<string, string> = { STANDARD: '0.15', ZERO: '0', EXEMPT: '0' }

export function priceLine(l: Pick<Line, 'item' | 'qty' | 'cost' | 'discount'>) {
  const gross = l.item && l.qty && l.cost ? lineAmount(l.qty, l.cost) : '0.00'
  const net = subMoney(gross, l.discount || '0')
  const vat = l.item ? mulMoney(net, VAT[l.item.vat_category] ?? '0') : '0.00'
  return { gross, net, vat, total: addMoney(net, vat) }
}

export default function PurchaseEntry() {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const nav = useNavigate()
  const qc = useQueryClient()
  const { can } = useAuth()
  const balances = useLedgerBalances()

  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const items = useQuery({ queryKey: ['inv-items', '', '', 'active'],
                           queryFn: () => fetchInvItems({ active: true }) })

  const [supplier, setSupplier] = useState('')
  const [supplierNo, setSupplierNo] = useState('')
  const [date, setDate] = useState(today())
  // Blank: the server takes the supplier's credit days.
  const [due, setDue] = useState('')
  const [storeId, setStoreId] = useState<number | ''>('')
  const [cash, setCash] = useState(false)
  const [cashCode, setCashCode] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<Line[]>([blank()])
  const [error, setError] = useState<string | null>(null)
  const [dupe, setDupe] = useState<{ doc_no: string; doc_date: string } | null>(null)

  useEffect(() => {
    if (storeId === '' && stores.data?.length) {
      const wh = stores.data.find((s) => s.is_active && s.kind === 'WAREHOUSE') ?? stores.data[0]
      setStoreId(wh.id)
    }
  }, [stores.data, storeId])

  // Tell the clerk before forty lines are keyed, not after.
  useEffect(() => {
    setDupe(null)
    if (!supplier || !supplierNo.trim()) return
    const h = window.setTimeout(() => {
      checkPurchaseDuplicate(supplier, supplierNo.trim()).then(setDupe).catch(() => {})
    }, 350)
    return () => window.clearTimeout(h)
  }, [supplier, supplierNo])

  const filled = lines.filter((l) => l.item)
  const priced = filled.map(priceLine)
  const subtotal = addMoney('0', ...priced.map((p) => p.net))
  const vat = addMoney('0', ...priced.map((p) => p.vat))
  const total = addMoney(subtotal, vat)
  const dirty = !!supplier || filled.length > 0 || !!supplierNo

  const setLine = (key: number, patch: Partial<Line>) => setLines((ls) => {
    const next = ls.map((l) => (l.key === key ? { ...l, ...patch } : l))
    return next[next.length - 1].item ? [...next, blank()] : next
  })

  async function pickItem(key: number, it: InvItem | null, unit?: string) {
    if (!it) { setLine(key, { item: null, unit: '', qty: '', cost: '', discount: '', hint: undefined }); return }
    setLine(key, { item: it, unit: unit ?? it.base_unit, qty: '', cost: '', hint: undefined })
    focusCell(key, 'qty')
    try {
      const last = await fetchLastCost(it.code, supplier || undefined)
      if (!last) return
      // Bought by the carton last time: offer the carton again, unless a
      // scanned barcode has already said which pack this is.
      const known = last.unit === it.base_unit || it.units.some((u) => u.unit === last.unit)
      const u = unit ?? (known ? last.unit : it.base_unit)
      const cost = last.unit === u ? Number(last.unit_cost)
        : Number(last.base_cost) * factorOf(it, u)
      setLine(key, {
        unit: u, cost: String(+cost.toFixed(4)),
        hint: t('inv.lastPaid', { cost: (+Number(last.unit_cost).toFixed(4)).toString(),
                                  unit: last.unit, supplier: last.supplier_name }),
      })
    } catch { /* a missing hint is not an error */ }
  }

  function changeUnit(l: Line, u: string) {
    if (!l.item) return
    // Keep the price per piece the same when the unit changes.
    const from = factorOf(l.item, l.unit), to = factorOf(l.item, u)
    const cost = l.cost ? String(+(Number(l.cost) / from * to).toFixed(4)) : ''
    setLine(l.key, { unit: u, cost })
  }

  const cells = useRef<Record<string, HTMLInputElement | null>>({})
  function focusCell(key: number, col: string) {
    requestAnimationFrame(() => cells.current[`${key}:${col}`]?.focus())
  }
  const ORDER = ['item', 'qty', 'cost', 'discount']
  function onEnter(e: React.KeyboardEvent, l: Line, col: string) {
    if (e.key !== 'Enter' || e.ctrlKey || e.metaKey) return
    e.preventDefault()
    const i = ORDER.indexOf(col)
    if (i < ORDER.length - 1) { focusCell(l.key, ORDER[i + 1]); return }
    const idx = lines.findIndex((x) => x.key === l.key)
    const nextLine = lines[idx + 1]
    if (nextLine) focusCell(nextLine.key, 'item')
  }

  const save = useMutation({
    mutationFn: () => createPurchase({
      supplier_code: supplier, supplier_invoice_no: supplierNo.trim() || null,
      doc_date: date, due_date: cash ? null : (due || null), store_id: Number(storeId),
      cash_ledger_code: cash ? cashCode : null, notes: notes.trim() || null,
      lines: filled.map((l) => ({ item_code: l.item!.code, qty: l.qty, unit: l.unit,
                                  unit_cost: l.cost, discount: l.discount || null })),
    }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['inv-items'] })
      void qc.invalidateQueries({ queryKey: ['inv-purchases'] })
      void qc.invalidateQueries({ queryKey: ['ledger-balances'] })
      setLines([blank()]); setSupplier(''); setSupplierNo('')
      nav(`/inventory/purchases/${p.id}`, { replace: true, state: { saved: true } })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  useUnsavedChanges(dirty && !save.isPending && !save.isSuccess)

  const problems = [
    !supplier && t('inv.needSupplier'),
    filled.length === 0 && t('inv.needLine'),
    filled.some((l) => !l.qty || Number(l.qty) <= 0) && t('inv.needQty'),
    filled.some((l) => l.cost === '') && t('inv.needCost'),
    cash && !cashCode && t('inv.needCash'),
    storeId === '' && t('inv.needStore'),
  ].filter(Boolean) as string[]
  const ready = problems.length === 0 && !dupe

  useHotkeys('purchase-entry', [
    { combo: 'ctrl+enter', label: t('common.save'), allowInInput: true,
      handler: () => { if (ready && !save.isPending) save.mutate() } },
    { combo: 'alt+a', label: t('inv.addLine'), allowInInput: true,
      handler: () => { const b = blank(); setLines((ls) => [...ls, b]); focusCell(b.key, 'item') } },
  ])

  const options = items.data?.items ?? []
  const typed = useRef<Record<number, string>>({})

  if (!can(P.postPurchase)) {
    return <Alert severity="warning">{t('report.noPermission')}</Alert>
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        <ScreenTitle titleKey="inv.newPurchase" sub={t('inv.newPurchaseSub')} />

        <Box sx={{ px: 2.5, pt: 1, pb: 2, display: 'grid', gap: 2,
                   gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '2fr 1.2fr 1fr 1fr' } }}>
          <Box sx={{ gridColumn: { sm: 'span 2', lg: 'auto' } }}>
            <Typography sx={{ fontSize: 12, color: c.inkMuted, mb: 0.5 }}>{t('inv.supplier')}</Typography>
            <LedgerPicker value={supplier} onChange={(code) => setSupplier(code)}
              groups={['CREDIT']} autoFocus placeholder={t('inv.supplierPlaceholder')}
              balanceOf={balances.of} />
          </Box>
          <TextField label={t('inv.supplierInvoiceNo')} value={supplierNo}
            onChange={(e) => setSupplierNo(e.target.value)} inputProps={{ maxLength: 40 }}
            error={!!dupe} helperText={dupe ? t('inv.duplicateNo', { doc: dupe.doc_no }) : t('inv.supplierInvoiceHint')} />
          <Box>
            <TextField type="date" label={t('entry.date')} value={date} fullWidth
              InputLabelProps={{ shrink: true }} onChange={(e) => setDate(e.target.value)} />
            <Typography sx={{ fontSize: 11.5, color: c.inkMuted, mt: 0.4, px: 0.25 }}>
              {date ? toHijri(date, i18n.language) : ''}</Typography>
          </Box>
          <TextField select label={t('inv.intoStore')} value={storeId}
            onChange={(e) => setStoreId(Number(e.target.value))}>
            {(stores.data ?? []).filter((s) => s.is_active).map((s) => (
              <MenuItem key={s.id} value={s.id}>{(ar && s.name_ar) || s.name_en}</MenuItem>))}
          </TextField>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}
          sx={{ px: 2.5, pb: 2 }}>
          <ToggleButtonGroup exclusive size="small" value={cash ? 'cash' : 'credit'}
            onChange={(_, v) => v && setCash(v === 'cash')}>
            <ToggleButton value="credit">{t('inv.onCredit')}</ToggleButton>
            <ToggleButton value="cash">{t('inv.paidNow')}</ToggleButton>
          </ToggleButtonGroup>
          {cash ? (
            <Box sx={{ flex: 1, maxWidth: 360 }}>
              <LedgerPicker value={cashCode} onChange={(code) => setCashCode(code)}
                groups={['CASH', 'BANK']} placeholder={t('inv.paidFrom')} balanceOf={balances.of} />
            </Box>
          ) : (
            <TextField type="date" size="small" label={t('inv.dueDate')} value={due}
              InputLabelProps={{ shrink: true }} sx={{ width: 190 }}
              helperText={due ? undefined : t('inv.dueHint')}
              onChange={(e) => setDue(e.target.value)} />
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ overflowX: 'auto', borderTop: `1px solid ${c.border}` }}>
          <table className="rpt" style={{ minWidth: 960 }}>
            <thead><tr>
              <th style={{ width: 32 }}>#</th>
              <th>{t('inv.item')}</th>
              <th style={{ width: 104 }}>{t('inv.unit')}</th>
              <th className="num" style={{ width: 90 }}>{t('inv.qty')}</th>
              <th className="num" style={{ width: 110 }}>{t('inv.costPerUnit')}</th>
              <th className="num" style={{ width: 96 }}>{t('inv.discount')}</th>
              <th className="num" style={{ width: 110 }}>{t('inv.net')}</th>
              <th className="num" style={{ width: 90 }}>{t('inv.vat')}</th>
              <th style={{ width: 40 }} />
            </tr></thead>
            <tbody>
              {lines.map((l, idx) => {
                const p = priceLine(l)
                const units = l.item ? [l.item.base_unit, ...l.item.units.map((u) => u.unit)] : []
                return (
                  <tr key={l.key}>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <Autocomplete<InvItem> size="small" options={options} value={l.item}
                        autoHighlight openOnFocus={false}
                        getOptionLabel={(o) => `${o.code} — ${(ar && o.name_ar) || o.name_en}`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onInputChange={(_, v) => { typed.current[l.key] = v }}
                        filterOptions={(opts, s) => {
                          const n = s.inputValue.trim().toLowerCase()
                          if (!n) return opts.slice(0, 50)
                          return opts.filter((o) => o.code.toLowerCase().includes(n)
                            || o.name_en.toLowerCase().includes(n) || (o.name_ar ?? '').includes(s.inputValue.trim())
                            || o.barcode === n || o.units.some((u) => u.barcode === n)).slice(0, 50)
                        }}
                        onChange={(_, v) => {
                          const scanned = (typed.current[l.key] ?? '').trim()
                          const pack = v?.units.find((u) => u.barcode && u.barcode === scanned)
                          void pickItem(l.key, v, pack?.unit)
                        }}
                        renderOption={(props, o) => (
                          <li {...props} key={o.id}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: 14 }} noWrap>
                                <span className="doc-no">{o.code}</span> · {(ar && o.name_ar) || o.name_en}</Typography>
                              <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                                {t('inv.onHandShort', { qty: Number(o.on_hand).toLocaleString('en-US'), unit: o.base_unit })}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={(pr) => <TextField {...pr} placeholder={t('inv.pickItem')}
                          inputRef={(el) => { cells.current[`${l.key}:item`] = el }}
                          inputProps={{ ...pr.inputProps,
                            'aria-label': t('inv.itemOnLine', { n: idx + 1 }) }} />} />
                      {l.hint && (
                        <Typography sx={{ fontSize: 11.5, color: c.inkMuted, px: 0.5, pt: 0.25 }}>
                          {l.hint}</Typography>
                      )}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <TextField select size="small" fullWidth value={l.item ? l.unit : ''}
                        disabled={!l.item} onChange={(e) => changeUnit(l, e.target.value)}
                        inputProps={{ 'aria-label': t('inv.unitOnLine', { n: idx + 1 }) }}>
                        {units.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                    </td>
                    {(['qty', 'cost', 'discount'] as const).map((col) => (
                      <td key={col} style={{ padding: '4px 8px' }}>
                        <TextField size="small" value={l[col]} disabled={!l.item}
                          inputRef={(el) => { cells.current[`${l.key}:${col}`] = el }}
                          onKeyDown={(e) => onEnter(e, l, col)}
                          onChange={(e) => setLine(l.key, { [col]: sanitize(e.target.value) })}
                          inputProps={{ inputMode: 'decimal', className: 'num',
                            style: { textAlign: 'end' },
                            'aria-label': t(`inv.${col}OnLine`, { n: idx + 1 }) }} />
                      </td>
                    ))}
                    <td className="num">{l.item ? fmtMoney(p.net) : ''}</td>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>
                      {l.item ? fmtMoney(p.vat) : ''}</td>
                    <td>
                      {l.item && (
                        <IconButton size="small" aria-label={t('inv.removeLine')}
                          onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                          <DeleteIcon fontSize="small" /></IconButton>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ px: 2.5, py: 2 }}
          alignItems={{ sm: 'flex-start' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => { const b = blank(); setLines((ls) => [...ls, b]); focusCell(b.key, 'item') }}>
              {t('inv.addLine')}</Button>
            <Keycap combo="alt+a" />
          </Stack>
          <TextField size="small" label={t('inv.notes')} value={notes} sx={{ flex: 1 }}
            onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 300 }} />
        </Stack>
      </Paper>

      {/* Totals, under the lines: the figure to check against the paper. */}
      <Paper variant="outlined" sx={{ p: 2.5, display: 'flex', flexWrap: 'wrap', gap: 3,
                                      alignItems: 'center' }}>
        <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={{ flex: '1 1 360px' }}>
          {[
            [t('inv.lines'), String(filled.length)],
            [t('inv.subtotal'), fmtMoney(subtotal)],
            [t('inv.vat'), fmtMoney(vat)],
          ].map(([k, v]) => (
            <Box key={k}>
              <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{k}</Typography>
              <Typography className="num" sx={{ fontSize: 16, fontWeight: 500, textAlign: 'start' }}>{v}</Typography>
            </Box>
          ))}
          <Box>
            <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{t('inv.total')}</Typography>
            <Typography className="num" sx={{ fontSize: 24, fontWeight: 600, textAlign: 'start' }}>
              SAR {fmtMoney(total)}</Typography>
          </Box>
        </Stack>
        <Box sx={{ flex: '1 1 300px', maxWidth: 420 }}>
          <Button fullWidth variant="contained" size="large" disabled={!ready || save.isPending}
            onClick={() => save.mutate()}>
            {t('inv.savePurchase')}
            <Box component="span" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
              <Keycap combo="ctrl+enter" /></Box>
          </Button>
          <Typography sx={{ fontSize: 12, color: problems.length && filled.length ? c.warningInk : c.inkMuted,
                            mt: 1 }}>
            {problems.length && filled.length ? problems[0]
              : cash ? t('inv.postsCash') : t('inv.postsCredit')}</Typography>
        </Box>
      </Paper>
    </Stack>
  )
}

function factorOf(it: InvItem, unit: string): number {
  if (unit === it.base_unit) return 1
  return Number(it.units.find((u) => u.unit === unit)?.factor ?? 1)
}
